import { useEffect } from "react";
import { createPortal } from "react-dom";
import {
    AppId,
    Button,
    ButtonType,
    FCX,
    getFormattedThousand,
    ProductLogo,
    sanitize,
    usePlatformContext,
} from "@datassential/platform-ui-lib";
import Widget from "@components/Widget/Widget";
import PublicationCard, { PublicationCardTooltip } from "@components/PublicationCard/PublicationCard";
import { IFoodProfileDocuments } from "@models/FoodProfileDocuments";
import { FoodSectionEnum } from "@pages/Food/Food.constants";
import FoodSectionTitle from "@pages/Food/refresh/components/FoodSectionTitle/FoodSectionTitle";
import "./FoodReportsSection.scss";

interface Props {
    word: string;
    data: IFoodProfileDocuments["reports"];
    isLoading: boolean;
    setHasData: (hasData: boolean) => void;
}

/** "Reports: <word>" — ReportPro publication covers (as many as fit in one row) plus a "view all" link. */
const FoodReportsSection: FCX<Props> = ({
    word,
    data: {
        data: {
            reports,
            totalCount,
        },
    },
    isLoading,
    setHasData,
}) => {
    const {
        userInfo: {
            products,
            reportProDetailsUrl,
            reportProMoreResultsUrl,
        },
    } = usePlatformContext();
    // Without a ReportPro product there is nowhere to link the covers to, so the section is hidden.
    const productRP = products[AppId.ReportPro];
    const hasReports = !!productRP && totalCount > 0;

    const detailsPath = process.env.REACT_APP_RP_ITEM_LINK ?? reportProDetailsUrl;
    const moreResultsPath = process.env.REACT_APP_RP_SEARCH_LINK ?? reportProMoreResultsUrl;

    useEffect(() => {
        setHasData(!isLoading && hasReports);
    }, [isLoading, hasReports, setHasData]);

    if (isLoading) {
        return (
            <div className="Food__section is-loading">
                <div className="spinner spinner-blink">
                    <ProductLogo appId={AppId.Snap} type="regular"/>
                </div>
            </div>
        );
    }

    if (!hasReports) return null;

    return (
        <div
            className="Food__section"
            id={FoodSectionEnum.Reports}
            data-testid="blockFoodSectionReports"
        >
            <div className="FoodReportsSection" data-testid="blockFoodReports">
                <FoodSectionTitle
                    className="FoodReportsSection__title"
                    title={`Reports: ${word}`}
                    testId="textFoodReportsTitle"
                />
                <div className="FoodReportsSection__card">
                    <Widget className="FoodReportsSection__panel">
                        <div className="FoodReportsSection__content">
                            <div className="FoodReportsSection__covers">
                                {reports.map((report, index) => (
                                    <PublicationCard
                                        key={report.id}
                                        data={report}
                                        index={index}
                                        link={sanitize(`${productRP.url}${detailsPath}${report.id}`)}
                                        tooltipId={`FP-reports-item-${report.id}`}
                                        testIdBase="FoodReportsItem"
                                    />
                                ))}
                            </div>
                            <div className="FoodReportsSection__actions">
                                <Button
                                    type={ButtonType.Anchor}
                                    link={sanitize(`${productRP.url}${moreResultsPath}${word}`)}
                                    className="FoodReportsSection__button"
                                    data-testid="buttonFoodReportsAllResultsExternalLink"
                                >
                                    view all {getFormattedThousand(totalCount)} documents
                                </Button>
                            </div>
                        </div>
                        {/* Portaled out of the section: the shared `.Food__section:hover` z-index makes the section a
                            stacking context, which would keep the tooltips under the sticky nav. */}
                        {createPortal(
                            <div className="FoodReportsSection__tooltips" data-testid="blockFoodReportsTooltips">
                                {reports.map((report, index) => (
                                    <PublicationCardTooltip
                                        key={report.id}
                                        data={report}
                                        index={index}
                                        tooltipId={`FP-reports-item-${report.id}`}
                                        testIdBase="FoodReportsItem"
                                        className="FoodReportsSection__tooltip"
                                    />
                                ))}
                            </div>,
                            document.body,
                        )}
                    </Widget>
                </div>
            </div>
        </div>
    );
};

export default FoodReportsSection;
